// src/cockpit4/components/BrandDiagnosisPanel.tsx
// ============================================
// ③ 品牌诊断 - 输入品牌名，跨平台综合诊断
//
// 功能：输入「品牌名」，逐平台询问 6 大 AI，
//       综合输出：提及率 / 首推率 / 综合得分（0-100）/ 谁最认可你（排名）/ 优化建议。
//
// 数据来源（真实，无 mock）：
//   · 走 GEODiagnosticRuntime（服务端代理 /{platform}-api 真实 API + DeepSeek 兜底）
//   · 品牌实体词优先从 Obsidian 品牌圣经解析
// ============================================

import { useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Fingerprint,
  Send,
  RefreshCw,
  Trophy,
  Activity,
  CheckCircle2,
  XCircle,
  ShieldCheck,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import { DIAG_PLATFORMS, DIAG_PLATFORM_MAP } from "../../runtime/GrowthFactory/geoPlatforms";
import DiagPlatformSelector from "./DiagPlatformSelector";
import type {
  BrandDiagnosisResult,
  DiagPlatformKey,
} from "../../runtime/GrowthFactory/types";
import type { GEODiagnosticRuntime } from "../../runtime/GrowthFactory/GEODiagnosticRuntime";

type Stage = "idle" | "running" | "done" | "error";

export default function BrandDiagnosisPanel() {
  const [brand, setBrand] = useState("小舍科技");
  const [selected, setSelected] = useState<DiagPlatformKey[]>(
    DIAG_PLATFORMS.map((p) => p.key)
  );
  const [stage, setStage] = useState<Stage>("idle");
  const [progress, setProgress] = useState("");
  const [result, setResult] = useState<BrandDiagnosisResult | null>(null);
  const [error, setError] = useState("");
  const abortRef = useRef(false);

  const getRuntime = () =>
    RuntimeManager.getRuntime(RuntimeName.GEODiagnostic) as unknown as GEODiagnosticRuntime;

  const runDiagnosis = async () => {
    const b = brand.trim();
    if (!b) {
      setError("请输入品牌名称");
      setStage("error");
      return;
    }
    if (selected.length === 0) {
      setError("请至少勾选 1 个诊断平台");
      setStage("error");
      return;
    }
    abortRef.current = false;
    setError("");
    setResult(null);
    setStage("running");
    try {
      const data = await getRuntime().diagnoseBrand(b, selected, (msg) =>
        setProgress(msg)
      );
      if (abortRef.current) return;
      setResult(data);
      setStage("done");
    } catch (e: any) {
      if (abortRef.current) return;
      setError(e?.message || String(e));
      setStage("error");
    }
  };

  const score = result?.diagnosisScore || 0;
  const scoreColor = score >= 70 ? "#34D399" : score >= 45 ? "#FBBF24" : "#F87171";
  const ringPercent = (score / 100) * 100;

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* 顶栏 */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Fingerprint size={16} className="text-emerald-400" />
            <span className="text-[13px] font-semibold text-white tracking-tight">
              ③ 品牌诊断
            </span>
          </div>
          <p className="text-[10px] font-mono text-gray-500 mt-1">
            输入品牌名 → 实时询问 6 大 AI → 跨平台综合诊断（提及率 / 首推率 / 心智占位排名）
          </p>
        </div>
      </div>

      {/* 输入区 */}
      <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3 flex flex-col gap-2.5">
        <label className="text-[10px] font-mono text-gray-500">品牌 / 公司名称</label>
        <div className="flex items-center gap-2">
          <input
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            placeholder="例如：小舍科技"
            className="flex-1 bg-black/20 border border-white/[0.08] rounded-md px-3 py-2 text-[12px] text-white placeholder:text-gray-600 focus:outline-none focus:border-emerald-500/50"
          />
          <motion.button
            whileTap={{ scale: 0.96 }}
            onClick={runDiagnosis}
            disabled={stage === "running"}
            className="flex items-center gap-1.5 px-4 py-2 rounded-md text-[11px] font-medium
                       bg-emerald-500/15 text-emerald-300 border border-emerald-500/30
                       hover:bg-emerald-500/25 disabled:opacity-50 transition-colors"
          >
            {stage === "running" ? (
              <RefreshCw size={13} className="animate-spin" />
            ) : (
              <Send size={13} />
            )}
            {stage === "running" ? "诊断中…" : "开始诊断"}
          </motion.button>
        </div>
        {error && <div className="text-[11px] text-red-400">{error}</div>}
      </div>

      {/* 品牌勾选接入区 */}
      <DiagPlatformSelector selected={selected} onChange={setSelected} />

      {progress && (
        <div className="text-[11px] font-mono text-cyan-300/80">
          <Activity size={12} className="inline mr-1" />
          <span className="text-cyan-400">▸</span> {progress}
        </div>
      )}

      {stage === "idle" && (
        <div className="text-[11px] text-gray-500 py-8 text-center border border-dashed border-white/[0.06] rounded-lg">
          输入品牌名称，勾选要接入诊断的 AI 厂家，实时看这些 AI 对该品牌的认知——是否提及、是否首推、综合得分与优化建议。
        </div>
      )}

      {result && (
        <>
          {/* 汇总看板 */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            {/* 综合得分环 */}
            <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3 flex flex-col items-center justify-center">
              <div className="relative w-16 h-16">
                <svg viewBox="0 0 64 64" className="w-16 h-16 -rotate-90">
                  <circle cx="32" cy="32" r="26" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="7" />
                  <motion.circle
                    cx="32"
                    cy="32"
                    r="26"
                    fill="none"
                    stroke={scoreColor}
                    strokeWidth="7"
                    strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 26}
                    initial={{ strokeDashoffset: 2 * Math.PI * 26 }}
                    animate={{ strokeDashoffset: 2 * Math.PI * 26 * (1 - ringPercent / 100) }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                  />
                </svg>
                <div
                  className="absolute inset-0 flex items-center justify-center text-[16px] font-bold"
                  style={{ color: scoreColor }}
                >
                  {score}
                </div>
              </div>
              <div className="text-[10px] text-gray-400 mt-1.5">综合得分</div>
            </div>
            <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
              <div className="text-[18px] font-bold tracking-tight text-cyan-400">
                {Math.round(result.mentionRate * 100)}%
              </div>
              <div className="text-[11px] text-gray-300 mt-0.5">提及率</div>
              <div className="text-[9px] font-mono text-gray-600 mt-0.5">
                {result.mentionCount}/{result.total} 个 AI 认识你
              </div>
            </div>
            <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
              <div className="text-[18px] font-bold tracking-tight text-amber-400">
                {Math.round(result.firstRate * 100)}%
              </div>
              <div className="text-[11px] text-gray-300 mt-0.5">首推率</div>
              <div className="text-[9px] font-mono text-gray-600 mt-0.5">
                {result.firstCount} 个 AI 开头即推荐
              </div>
            </div>
            <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
              <div className="text-[18px] font-bold tracking-tight text-violet-400">
                {result.ranking.length}
              </div>
              <div className="text-[11px] text-gray-300 mt-0.5">认可你的 AI 数</div>
              <div className="text-[9px] font-mono text-gray-600 mt-0.5">按提及次数排名</div>
            </div>
            <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
              <div className="text-[14px] font-bold tracking-tight text-white truncate">
                {result.brand?.slice(0, 10) || "-"}
              </div>
              <div className="text-[11px] text-gray-300 mt-0.5">诊断品牌</div>
              <div className="text-[9px] font-mono text-gray-600 mt-0.5">
                {result.checkedAt?.slice(11, 19) || ""} 执行
              </div>
            </div>
          </div>

          {/* 最认可你的 AI 排名 */}
          <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
            <div className="flex items-center gap-2 mb-2">
              <Trophy size={14} className="text-amber-400" />
              <span className="text-[12px] font-semibold text-white">谁最认可你（心智占位排名）</span>
              <span className="text-[9px] font-mono text-gray-600">按提及次数 + 位次排序</span>
            </div>
            {result.ranking.length === 0 ? (
              <div className="text-[11px] text-gray-500 py-3 text-center">
                没有 AI 提及该品牌 —— AI 心智占位为零，需立即启动 GEO 内容铺设。
              </div>
            ) : (
              result.ranking.map((r, idx) => {
                const p = DIAG_PLATFORM_MAP[r.platform as DiagPlatformKey];
                const width = Math.max(5, Math.round((r.count / (result.ranking[0]?.count || 1)) * 100));
                const medals = ["#F59E0B", "#CBD5E1", "#D97706"];
                return (
                  <div key={r.platform} className="flex items-center gap-3 py-1.5">
                    <span className="w-5 text-center">
                      {idx < 3 ? (
                        <Trophy size={13} style={{ color: medals[idx] }} />
                      ) : (
                        <span className="text-[10px] font-mono text-gray-600">{idx + 1}</span>
                      )}
                    </span>
                    <span
                      className="w-7 h-7 rounded-md flex items-center justify-center text-[11px] font-bold text-white"
                      style={{ background: p.color }}
                    >
                      {p.short}
                    </span>
                    <span className="w-20 text-[11px] text-gray-300">{p.name}</span>
                    <div className="flex-1 h-3 rounded bg-white/[0.04] overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${width}%` }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                        className="h-full rounded"
                        style={{ background: `linear-gradient(90deg, ${p.color}66, ${p.color})` }}
                      />
                    </div>
                    <span className="w-16 text-right text-[11px] font-mono text-white">
                      {r.count} 次
                    </span>
                    <span className="w-12 text-right text-[10px] font-mono text-gray-600">
                      {r.first ? "首推" : ""}
                    </span>
                  </div>
                );
              })
            )}
          </div>

          {/* 诊断结论 */}
          <div
            className={`rounded-lg border p-3 flex items-start gap-2 ${
              result.diagnosisScore >= 70
                ? "bg-emerald-500/[0.06] border-emerald-500/20"
                : result.diagnosisScore >= 45
                ? "bg-amber-500/[0.06] border-amber-500/20"
                : "bg-red-500/[0.06] border-red-500/20"
            }`}
          >
            <ShieldCheck size={16} className="text-emerald-400 mt-0.5 shrink-0" />
            <div className="text-[12px] text-gray-200">
              <span className="font-semibold text-white">诊断结论：</span>
              {result.verdict}
              <div className="text-[10px] font-mono text-gray-500 mt-1">
                信源校验：www.xshetech.com · CSDN · 知乎 · 头条（sameAs 已挂载）
              </div>
            </div>
          </div>

          {/* 各平台明细 */}
          <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
            <div className="flex items-center gap-2 mb-3">
              <Activity size={14} className="text-emerald-400" />
              <span className="text-[12px] font-semibold text-white">各 AI 对品牌的说法</span>
              <span className="text-[9px] font-mono text-gray-600">提及 / 位次 / 引用片段</span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
              {result.results.map((r) => {
                const p = DIAG_PLATFORM_MAP[r.platform as DiagPlatformKey];
                return (
                  <div
                    key={r.platform}
                    className={`rounded-md border p-2.5 ${
                      r.mentioned
                        ? "bg-emerald-500/[0.04] border-emerald-500/20"
                        : "bg-white/[0.01] border-white/[0.06]"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <span
                        className="w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold text-white"
                        style={{ background: p.color }}
                      >
                        {p.short}
                      </span>
                      <span className="text-[11px] font-medium text-white flex-1">{p.name}</span>
                      {r.mentioned ? (
                        <span className="flex items-center gap-1 text-[10px] text-emerald-400">
                          <CheckCircle2 size={12} />
                          {r.first ? "首推" : r.top3 ? "前3推荐" : "已提及"} · {r.count} 次
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-[10px] text-gray-500">
                          <XCircle size={12} />
                          未提及
                        </span>
                      )}
                    </div>
                    {r.mentioned && (
                      <div className="text-[11px] text-gray-300 leading-relaxed bg-black/20 rounded p-2 border border-white/[0.04]">
                        “…{r.snippet}…”
                      </div>
                    )}
                    {r.error && <div className="text-[10px] text-red-400/80">{r.error}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
